import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Clock, MapPin, CalendarClock, Check } from "lucide-react";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString("da-DK", { weekday: "short", day: "numeric", month: "short" }); }
  catch { return s; }
};

export function JatakSchedule() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [time, setTime] = useState("");
  const [store, setStore] = useState("");
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/jatak/schedule"); setItems(data); }
    catch { /* stille */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim()) { toast.error("Skriv hvad opslaget handler om"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/jatak/schedule", { title, time, store, date });
      setItems((xs) => [...xs, data]);
      setTitle(""); setTime("");
      toast.success("Tilføjet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke tilføje"); }
    finally { setSaving(false); }
  };

  const toggle = async (it) => {
    try {
      const { data } = await api.post(`/jatak/schedule/${it.id}/toggle`);
      setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, done: data.done } : x)));
    } catch { toast.error("Kunne ikke opdatere"); }
  };

  const remove = async (it) => {
    try { await api.delete(`/jatak/schedule/${it.id}`); setItems((xs) => xs.filter((x) => x.id !== it.id)); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const sorted = [...items].sort((a, b) =>
    (a.date || "").localeCompare(b.date || "") || (a.time || "").localeCompare(b.time || ""));

  return (
    <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="jatak-schedule">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
        <CalendarClock className="w-4 h-4 text-emerald-600" />
        Mine Ja tak-opslag · hvad har jeg lagt op og hvornår
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto,auto,auto,auto] gap-2 mb-4">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Fx Lakrids mix 800g" data-testid="schedule-title" />
        <Input type="time" value={time} onChange={(e) => setTime(e.target.value)}
          className="w-full sm:w-28" data-testid="schedule-time" />
        <select value={store} onChange={(e) => setStore(e.target.value)} data-testid="schedule-store"
          className="h-10 rounded-md border border-[#e2e8f0] bg-white px-2 text-sm text-slate-700">
          <option value="">Butik…</option>
          <option value="Falster">Falster</option>
          <option value="Ringsted">Ringsted</option>
          <option value="Begge">Begge</option>
        </select>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="w-full sm:w-40" data-testid="schedule-date" />
        <Button onClick={add} disabled={saving} className="rounded-lg gap-1.5" data-testid="schedule-add">
          <Plus className="w-4 h-4" />Tilføj
        </Button>
      </div>

      {sorted.length === 0 ? (
        <p className="text-sm text-slate-400 py-2" data-testid="schedule-empty">Ingen planlagte opslag endnu — skriv det første ovenfor.</p>
      ) : (
        <div className="space-y-2" data-testid="schedule-list">
          {sorted.map((it) => (
            <div key={it.id} data-testid="schedule-item"
              className={`flex items-center gap-3 rounded-xl border p-2.5 ${it.done ? "bg-emerald-50 border-emerald-200" : "bg-[#f8fafc] border-[#e2e8f0]"}`}>
              <button onClick={() => toggle(it)} data-testid="schedule-toggle"
                className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 transition-colors ${it.done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300 text-transparent hover:border-emerald-400"}`}>
                <Check className="w-4 h-4" />
              </button>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium truncate ${it.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{it.title}</p>
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  {it.date && <span className="text-xs text-slate-400">{fmtDate(it.date)}</span>}
                  {it.time && <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 border border-amber-500/25"><Clock className="w-3 h-3" />{it.time}</span>}
                  {it.store && <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-sky-500/10 text-sky-700 border border-sky-500/20"><MapPin className="w-3 h-3" />{it.store}</span>}
                </div>
              </div>
              <button onClick={() => remove(it)} data-testid="schedule-delete"
                className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
